(() => {
  'use strict';

  const GALLERY_SELECTOR = '.Product__Gallery';
  const SLIDE_SELECTOR = '.Product__SlideItem[data-media-id]';
  const THUMBNAILS_SELECTOR = '.Product__SlideshowNav';

  let viewer = null;
  let items = [];
  let currentIndex = 0;

  const imageCache = new Map();

  function getImageSource(image) {
    if (!(image instanceof HTMLImageElement)) {
      return '';
    }

    return (
      image.getAttribute('data-original-src') ||
      image.getAttribute('data-src') ||
      image.currentSrc ||
      image.src ||
      ''
    );
  }

  function collectImages(gallery) {
    return Array.from(
      gallery.querySelectorAll(SLIDE_SELECTOR)
    )
      .map((slide) => {
        const image = slide.querySelector('img');

        if (!image) {
          return null;
        }

        const src = getImageSource(image);

        if (!src) {
          return null;
        }

        return {
          mediaId: String(slide.dataset.mediaId || ''),
          src,
          alt: image.alt || ''
        };
      })
      .filter(Boolean);
  }

  function preloadImage(item) {
    if (!item?.src || imageCache.has(item.src)) {
      return;
    }

    const image = new Image();

    image.decoding = 'async';
    image.src = item.src;

    imageCache.set(item.src, image);

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {
        // El navegador continuará cargando la imagen normalmente.
      });
    }
  }

  function preloadNearbyImages() {
    preloadImage(items[currentIndex - 1]);
    preloadImage(items[currentIndex + 1]);

    /*
     * Después de cargar las imágenes contiguas,
     * precargamos el resto sin bloquear la navegación.
     */
    window.setTimeout(() => {
      items.forEach(preloadImage);
    }, 200);
  }

  function createButton(label, symbol, styles) {
    const button = document.createElement('button');

    button.type = 'button';
    button.setAttribute('aria-label', label);
    button.textContent = symbol;

    Object.assign(button.style, {
      position: 'fixed',
      zIndex: '2147483647',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '48px',
      height: '48px',
      margin: '0',
      padding: '0 0 4px',
      border: '0',
      borderRadius: '50%',
      background: 'rgba(32, 32, 30, 0.86)',
      color: '#ffffff',
      fontFamily: 'Arial, sans-serif',
      fontSize: '36px',
      fontWeight: '300',
      lineHeight: '1',
      cursor: 'pointer',
      boxShadow: 'none',
      ...styles
    });

    return button;
  }

  function renderImage() {
    if (!viewer || !items[currentIndex]) {
      return;
    }

    const item = items[currentIndex];

    const image = viewer.querySelector(
      '[data-mooa-viewer-image]'
    );

    const counter = viewer.querySelector(
      '[data-mooa-viewer-counter]'
    );

    const previous = viewer.querySelector(
      '[data-mooa-viewer-previous]'
    );

    const next = viewer.querySelector(
      '[data-mooa-viewer-next]'
    );

    /*
     * Si la imagen ya está cargada, se muestra directamente.
     * Si no, mantenemos la anterior visible hasta que la nueva
     * esté preparada para evitar el parpadeo.
     */
    const cachedImage = imageCache.get(item.src);

    if (
      cachedImage &&
      cachedImage.complete &&
      cachedImage.naturalWidth > 0
    ) {
      image.alt = item.alt;
      image.src = item.src;
      image.style.opacity = '1';
    } else {
      const loader = cachedImage || new Image();

      if (!cachedImage) {
        loader.decoding = 'async';
        loader.src = item.src;
        imageCache.set(item.src, loader);
      }

      loader.onload = () => {
        if (
          !viewer ||
          items[currentIndex]?.src !== item.src
        ) {
          return;
        }

        image.alt = item.alt;
        image.src = item.src;

        requestAnimationFrame(() => {
          image.style.opacity = '1';
        });
      };

      /*
       * La imagen anterior permanece visible mientras carga.
       * No ponemos opacity: 0 para que el cambio se perciba
       * más rápido y no aparezca un hueco vacío.
       */
    }

    counter.textContent =
      `${currentIndex + 1} / ${items.length}`;

    previous.disabled = currentIndex === 0;
    next.disabled =
      currentIndex === items.length - 1;

    previous.style.opacity =
      previous.disabled ? '0.2' : '1';

    next.style.opacity =
      next.disabled ? '0.2' : '1';

    preloadNearbyImages();
  }

  function changeImage(direction) {
    const nextIndex = currentIndex + direction;

    if (
      nextIndex < 0 ||
      nextIndex >= items.length
    ) {
      return;
    }

    currentIndex = nextIndex;
    renderImage();
  }

  function closeViewer() {
    if (!viewer) {
      return;
    }

    viewer.remove();
    viewer = null;
    items = [];

    document.documentElement.style.removeProperty(
      'overflow'
    );

    document.body.style.removeProperty(
      'overflow'
    );
  }

  function openViewer(gallery, mediaId) {
    closeViewer();

    items = collectImages(gallery);

    if (items.length === 0) {
      return;
    }

    currentIndex = Math.max(
      0,
      items.findIndex(
        (item) =>
          item.mediaId === String(mediaId || '')
      )
    );

    /*
     * Empieza a cargar la foto seleccionada y las dos
     * contiguas antes incluso de construir el visor.
     */
    preloadImage(items[currentIndex]);
    preloadImage(items[currentIndex - 1]);
    preloadImage(items[currentIndex + 1]);

    document.documentElement.style.overflow =
      'hidden';

    document.body.style.overflow =
      'hidden';

    viewer = document.createElement('div');

    viewer.setAttribute('role', 'dialog');
    viewer.setAttribute('aria-modal', 'true');
    viewer.setAttribute(
      'aria-label',
      'Galería ampliada del producto'
    );

    Object.assign(viewer.style, {
      position: 'fixed',
      top: '0',
      right: '0',
      bottom: '0',
      left: '0',
      zIndex: '2147483600',
      display: 'block',
      width: '100vw',
      height: '100vh',
      margin: '0',
      padding: '0',
      overflow: 'hidden',
      background: '#f3f0e4',
      transform: 'none'
    });

    const stage = document.createElement('div');

    Object.assign(stage.style, {
      position: 'absolute',
      top: '24px',
      right: '82px',
      bottom: '24px',
      left: '82px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden'
    });

    const image = document.createElement('img');

    image.setAttribute(
      'data-mooa-viewer-image',
      ''
    );

    Object.assign(image.style, {
      position: 'static',
      display: 'block',
      width: 'auto',
      height: 'auto',
      maxWidth: '100%',
      maxHeight: '100%',
      margin: '0 auto',
      padding: '0',
      objectFit: 'contain',
      objectPosition: 'center',
      opacity: '1',
      transform: 'none',
      transition: 'opacity 0.12s ease',
      userSelect: 'none'
    });

    stage.appendChild(image);
    viewer.appendChild(stage);

    const close = createButton(
      'Cerrar',
      '×',
      {
        top: '18px',
        right: '18px'
      }
    );

    close.style.fontSize = '32px';

    const previous = createButton(
      'Imagen anterior',
      '‹',
      {
        top: '50%',
        left: '18px',
        transform: 'translateY(-50%)'
      }
    );

    previous.setAttribute(
      'data-mooa-viewer-previous',
      ''
    );

    const next = createButton(
      'Imagen siguiente',
      '›',
      {
        top: '50%',
        right: '18px',
        transform: 'translateY(-50%)'
      }
    );

    next.setAttribute(
      'data-mooa-viewer-next',
      ''
    );

    const counter =
      document.createElement('div');

    counter.setAttribute(
      'data-mooa-viewer-counter',
      ''
    );

    Object.assign(counter.style, {
      position: 'fixed',
      right: '20px',
      bottom: '18px',
      zIndex: '2147483647',
      padding: '7px 10px',
      borderRadius: '20px',
      background:
        'rgba(255, 255, 255, 0.88)',
      color: '#222222',
      fontSize: '12px',
      lineHeight: '1'
    });

    close.addEventListener(
      'click',
      closeViewer
    );

    previous.addEventListener(
      'click',
      () => changeImage(-1)
    );

    next.addEventListener(
      'click',
      () => changeImage(1)
    );

    viewer.appendChild(close);
    viewer.appendChild(previous);
    viewer.appendChild(next);
    viewer.appendChild(counter);

    document.body.appendChild(viewer);

    renderImage();
  }

  function handleGalleryClick(event) {
    const target =
      event.target instanceof Element
        ? event.target
        : null;

    if (!target) {
      return;
    }

    const gallery = target.closest(
      GALLERY_SELECTOR
    );

    if (!gallery) {
      return;
    }

    if (target.closest(THUMBNAILS_SELECTOR)) {
      return;
    }

    const clickedImage =
      target.closest('.Product__SlideItem img');

    const zoomButton =
      target.closest(
        '[data-action="open-product-zoom"]'
      );

    if (!clickedImage && !zoomButton) {
      return;
    }

    const slide =
      target.closest(SLIDE_SELECTOR) ||
      gallery.querySelector(
        `${SLIDE_SELECTOR}.is-selected`
      ) ||
      gallery.querySelector(
        SLIDE_SELECTOR
      );

    if (!slide) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    openViewer(
      gallery,
      slide.dataset.mediaId
    );
  }

  function handleKeyboard(event) {
    if (!viewer) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeViewer();
      return;
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      changeImage(-1);
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      changeImage(1);
    }
  }

  window.addEventListener(
    'click',
    handleGalleryClick,
    true
  );

  window.addEventListener(
    'keydown',
    handleKeyboard,
    true
  );
})();